import {
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { Alert, Box, Button, Chip, CircularProgress, Grid, IconButton, Stack } from '@mui/material';
import BackendService from 'BackendService';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReviewRequest, ReviewStatus } from '../../models/ReviewRequest';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';
import StyledSection from '../styles/StyledSection';
import { StyledCard, StyledCardContent, StyledHeading, StyledTypography } from '../styles/styles';

interface ReviewRequestsPageProps {
  tone?: keyof typeof Palette;
}

const getStatusColor = (status: ReviewStatus) => {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'CHANGES_REQUESTED':
      return 'error';
    case 'REVIEW_COMPLETE':
      return 'success';
    case 'REVIEW_REJECTED':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusIcon = (status: ReviewStatus) => {
  switch (status) {
    case 'PENDING':
      return <PendingIcon />;
    case 'CHANGES_REQUESTED':
      return <ReplayIcon />;
    case 'REVIEW_COMPLETE':
      return <CheckCircleIcon />;
    case 'REVIEW_REJECTED':
      return <ReplayIcon />;
    default:
      return <AssignmentIcon />;
  }
};

export default function ReviewRequestsPage({ tone = 'tone1' }: ReviewRequestsPageProps) {
  const [reviewRequests, setReviewRequests] = useState<ReviewRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadReviewRequests();
  }, []);

  const loadReviewRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const backendService = new BackendService();
      const response = await backendService.getReviewRequests();
      setReviewRequests(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load review requests');
      console.error('Error loading review requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewReview = (externalId: string) => {
    navigate(`/review/${externalId}`);
  };

  if (loading) {
    return (
      <StyledSection tone={tone}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </StyledSection>
    );
  }

  if (error) {
    return (
      <StyledSection tone={tone}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <StyledButton onClick={loadReviewRequests}>Retry</StyledButton>
      </StyledSection>
    );
  }

  return (
    <StyledSection tone={tone}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/actions')}>
          <ArrowBackIcon />
        </IconButton>
        <StyledHeading tone={tone}>Review Requests</StyledHeading>
      </Stack>

      {reviewRequests.length === 0 ? (
        <Alert severity="info">No review requests found.</Alert>
      ) : (
        <Grid container spacing={2}>
          {reviewRequests.map((request) => (
            <Grid item xs={12} md={6} lg={4} key={request.id}>
              <StyledCard tone={tone}>
                <StyledCardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {getStatusIcon(request.status)}
                      <Chip
                        label={request.status.replace('_', ' ')}
                        color={getStatusColor(request.status) as any}
                        size="small"
                      />
                    </Stack>

                    <Box>
                      <StyledTypography tone={tone} variant="h6">
                        {request.fundOpportunity.name}
                      </StyledTypography>
                      <StyledTypography tone={tone} color="text.secondary" variant="body2">
                        Requested by: {request.requestedBy.name}
                      </StyledTypography>
                      <StyledTypography tone={tone} color="text.secondary" variant="body2">
                        Assigned to: {request.requestedOf.name}
                      </StyledTypography>
                    </Box>

                    <Box>
                      <StyledTypography tone={tone} variant="body2" color="text.secondary">
                        Created: {format(new Date(request.createdAt), "MMM d, yyyy 'at' h:mm a")}
                      </StyledTypography>
                      <StyledTypography tone={tone} variant="body2" color="text.secondary">
                        Updated: {format(new Date(request.updatedAt), "MMM d, yyyy 'at' h:mm a")}
                      </StyledTypography>
                    </Box>

                    <Button
                      variant="contained"
                      onClick={() => handleViewReview(request.id)}
                      fullWidth
                    >
                      View Review
                    </Button>
                  </Stack>
                </StyledCardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      )}
    </StyledSection>
  );
}
